import React, { useEffect } from "react";
import {
    BrowserRouter,
    Routes,
    Route,
    useLocation,
} from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { AnnouncementBar } from "@/components/AnnouncementBar";

import Home from "@/pages/Home";
import Collection from "@/pages/Collection";
import Configurator from "@/pages/Configurator";
import Philosophy from "@/pages/Philosophy";
import Legal from "@/pages/Legal";
import CartPage from "@/pages/CartPage";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";
import PaymentSuccess from "@/pages/PaymentSuccess";

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [pathname]);
    return null;
}

function SmoothScroll() {
    useEffect(() => {
        // Disable smooth-scroll on touch devices — Lenis intercepts wheel/scroll
        // which adds perceptible lag to taps and slows down page navigation on mobile.
        const isTouch =
            typeof window !== "undefined" &&
            (window.matchMedia("(pointer: coarse)").matches ||
                "ontouchstart" in window);
        if (isTouch) return undefined;

        const lenis = new Lenis({
            duration: 0.9,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            smoothWheel: true,
        });
        let raf;
        const loop = (time) => {
            lenis.raf(time);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
        };
    }, []);
    return null;
}

function Layout({ children }) {
    return (
        <div className="min-h-screen bg-paper text-ink grain">
            <AnnouncementBar />
            <Navbar />
            <main>{children}</main>
            <Footer />
        </div>
    );
}

export default function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <CartProvider>
                    <SmoothScroll />
                    <ScrollToTop />
                    <Toaster
                        position="bottom-right"
                        toastOptions={{
                            style: {
                                borderRadius: 0,
                                border: "1px solid #E2DFD5",
                                background: "#FAFAF7",
                                color: "#131211",
                                fontFamily: "Inter, sans-serif",
                            },
                        }}
                    />
                    <Layout>
                        <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/shop/:category/:gender" element={<Collection />} />
                            <Route path="/configure/:category/:gender" element={<Configurator />} />
                            <Route path="/philosophy" element={<Philosophy />} />
                            <Route path="/legal" element={<Legal />} />
                            <Route path="/cart" element={<CartPage />} />
                            <Route path="/login" element={<Login />} />
                            <Route path="/register" element={<Register />} />
                            <Route path="/account" element={<Account />} />
                            <Route path="/admin" element={<Admin />} />
                            <Route path="/payment/success" element={<PaymentSuccess />} />
                        </Routes>
                    </Layout>
                </CartProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}
